import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Building2,
  MapPin,
  IndianRupee,
  TrendingUp,
  TrendingDown,
  ArrowLeft,
  Award,
  Activity,
} from 'lucide-react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import KVSLoader from '../../../components/loaders/KVSLoader';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import img1 from '../../../assets/pmshribanner.webp';

import {
  HeadWiseDonutChart,
  UtilizationGauge,
  TrendLineChart,
} from '../components/charts';

import { YearDropdown } from '../components/filters';

import type {
  SchoolPerformance,
  Head,
  SubActivity,
  HeadWiseSchoolData,
  MonthlyTrendData,
  HeadCode,
} from '../types';
import { DEFAULT_HEADS, HEAD_COLORS, PMShriPhase } from '../types';
import { useGetSchoolDetailsQuery, useGetSchoolHeadsBreakdownQuery, useGetSchoolActivityWiseDetailQuery, useGetSchoolExpenseDetailQuery, useGetSchoolMonthlyFundTrendQuery, useGetSchoolPillarWiseExpenditureQuery, useGetSchoolSanctionedHeadsBudgetQuery } from '../services/api';

type TabType = 'overview' | 'heads' | 'activities' | 'expenses' | 'pillars' | 'budget';

/** Money shape returned by the pillar-wise endpoint. */
type PillarMoneyUI = { rec: number; nonRec: number; total: number };

const SchoolDetailsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { kvId } = useParams<{ kvId: string }>();
  const carouselImages = [img1];

  // State
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [selectedYear, setSelectedYear] = useState<number | null>(null); // null = All Years
  const [activityFilter, setActivityFilter] = useState<'all' | 'recurring' | 'non-recurring'>('all');
  const [school, setSchool] = useState<(SchoolPerformance & { activities: SubActivity[]; trends: MonthlyTrendData[] }) | null>(null);

  // API Calls
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: apiResponse, isLoading, isFetching: overviewLoading, error: _error } = useGetSchoolDetailsQuery({
    kvid: parseInt(kvId || '0'),
    ...(selectedYear !== null && { year: selectedYear }),
  });

  const { data: headsBreakdownResponse, isFetching: headsLoading } = useGetSchoolHeadsBreakdownQuery({
    kvid: parseInt(kvId || '0'),
    ...(selectedYear !== null && { year: selectedYear }),
  });

  const { data: activityWiseResponse, isFetching: activitiesLoading } = useGetSchoolActivityWiseDetailQuery({
    kvid: parseInt(kvId || '0'),
    ...(selectedYear !== null && { year: selectedYear }),
  });

  const { data: expenseDetailResponse, isFetching: expensesLoading } = useGetSchoolExpenseDetailQuery({
    kvid: parseInt(kvId || '0'),
    ...(selectedYear !== null && { year: selectedYear }),
  });

  const { data: pillarWiseResponse, isFetching: pillarWiseLoading } = useGetSchoolPillarWiseExpenditureQuery({
    kvid: parseInt(kvId || '0'),
    ...(selectedYear !== null && { year: selectedYear }),
  });

  const { data: budgetResponse, isFetching: budgetLoading } = useGetSchoolSanctionedHeadsBudgetQuery({
    kvid: parseInt(kvId || '0'),
    ...(selectedYear !== null && { year: selectedYear }),
  });

  const { data: monthlyTrendResponse } = useGetSchoolMonthlyFundTrendQuery({
    kvid: parseInt(kvId || '0'),
    ...(selectedYear !== null && { year: selectedYear }),
  });

  /**
   * Whether the tab currently on screen is still fetching.
   *
   * isFetching, not isLoading: isLoading only covers the first time a cache
   * key is seen, so switching tabs (or changing the year) would otherwise show
   * the previous tab's numbers with nothing to say a request was in flight.
   * Each tab is keyed to its own query, so a slow tab never blocks a ready one.
   */
  const tabLoading: Record<TabType, boolean> = {
    overview: overviewLoading,
    heads: headsLoading,
    activities: activitiesLoading,
    expenses: expensesLoading,
    budget: budgetLoading,
    pillars: pillarWiseLoading,
  };
  const isTabLoading = tabLoading[activeTab];

  const formatCurrency = (value: number) => {
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 5, maximumFractionDigits: 5 })}`;
  };

  useEffect(() => {
    if (apiResponse?.success && apiResponse.data) {
      const { profile, financials, breakdown } = apiResponse.data;

      const utilizationVal = typeof financials.utilization === 'string'
        ? parseFloat(financials.utilization.replace('%', ''))
        : financials.utilization || 0;

      // Process breakdown data - prefer headsBreakdownResponse if available for year-specific data
      const breakdownSource = headsBreakdownResponse?.success && headsBreakdownResponse.data?.breakdown
        ? headsBreakdownResponse.data.breakdown
        : breakdown;

      // Ensure we always show all DEFAULT_HEADS (6 heads). If API breakdown misses any,
      // use defaults with zeroed values so the UI remains consistent.
      const headWiseData: HeadWiseSchoolData[] = (DEFAULT_HEADS || []).map((defHead, idx) => {
        const found = breakdownSource?.find((b: { head: string }) => {
          if (!b || !b.head) return false;
          const a = b.head.toString().trim().toLowerCase();
          const bname = defHead.name.toString().trim().toLowerCase();
          return a.includes(bname) || bname.includes(a);
        });

        const sanctioned = found?.sanctioned || 0;
        const spent = found?.spent || 0;
        const utilRaw = found?.utilization ?? '';
        const utilVal = typeof utilRaw === 'string' && utilRaw.includes('%')
          ? parseFloat(utilRaw.replace('%', ''))
          : (typeof utilRaw === 'number' ? utilRaw : (sanctioned > 0 ? (spent / sanctioned) * 100 : 0));

        return {
          headId: idx + 1,
          headCode: (defHead.code || `H${idx + 1}`) as HeadCode,
          headName: (found?.head && found.head.trim()) || defHead.name,
          sanctioned,
          received: sanctioned,
          spent,
          unspent: sanctioned - spent,
          utilization: utilVal,
          targetUnits: 0,
          completedUnits: 0,
          efficiency: utilVal,
        };
      });

      // Construct school object
      const schoolData: SchoolPerformance & { activities: SubActivity[]; trends: MonthlyTrendData[] } = {
        kvId: profile.schoolID,
        kvCode: `KV${profile.schoolID}`, // Fallback if no code in profile
        schoolName: profile.schoolName,
        kvName: profile.schoolName,
        roId: profile.roID,
        roName: profile.roName,
        region: profile.roName,
        stateId: profile.stateID,
        stateName: profile.stateName,
        districtName: profile.districtName,
        pmShriStatus: 'active', // Default
        pmShriPhase: profile.phase as PMShriPhase,
        totalSanctioned: financials.sanctionedAmount,
        totalReceived: financials.receivedAmount,
        totalSpent: financials.spentAmount,
        totalUnspent: financials.receivedAmount - financials.spentAmount,
        overallUtilization: utilizationVal,
        utilizationPercentage: utilizationVal,
        targetUnits: 0,
        completedUnits: 0,
        efficiencyPercentage: utilizationVal,
        /**
         * Utilisation is spent / SANCTIONED, matching the gauge, the ranking
         * panels and every other PM SHRI surface. These two cards previously
         * divided by RELEASED, which is why recurring read 231.2%: the school
         * spent 146.75 against 63.47 released, because its FY2025/26 releases
         * were never keyed in. Against the 159.36 actually sanctioned for
         * recurring heads it is 92.1%.
         *
         * Falls back to released only if the API predates sanctionedRec.
         */
        recurringAmount: financials.sanctionedRec ?? financials.recurringAmount,
        recurringSpent: financials.spentRec || 0,
        recurringUtilization: (financials.sanctionedRec ?? financials.recurringAmount) > 0
          ? (financials.spentRec / (financials.sanctionedRec ?? financials.recurringAmount)) * 100
          : 0,
        nonRecurringAmount: financials.sanctionedNonRec ?? financials.nonRecurringAmount,
        nonRecurringSpent: financials.spentNonRec || 0,
        nonRecurringUtilization: (financials.sanctionedNonRec ?? financials.nonRecurringAmount) > 0
          ? (financials.spentNonRec / (financials.sanctionedNonRec ?? financials.nonRecurringAmount)) * 100
          : 0,
        headWiseData: headWiseData,
        performanceScore: utilizationVal,
        rank: 0,
        previousRank: 0,
        trend: 'stable',
        activities: [], // Not provided in API
        trends: [], // Not provided in API
      };

      setSchool(schoolData);
    } else {
      setSchool(null);
    }
  }, [apiResponse, headsBreakdownResponse]);

  // Update activities when activity-wise API responds
  useEffect(() => {
    if (activityWiseResponse?.success && activityWiseResponse.data?.activityWise) {
      const activityWise = activityWiseResponse.data.activityWise;

      const activities: SubActivity[] = activityWise.map((item: { activityName: string; sanctioned: number; spent: number; utilization: string }, idx: number) => {
        const utilVal = typeof item.utilization === 'string'
          ? parseFloat(item.utilization.replace('%', ''))
          : item.utilization || 0;

        // Determine status based on utilization
        let status: 'fully_utilized' | 'partially_utilized' | 'not_utilized' | 'pending';
        if (utilVal >= 90) status = 'fully_utilized';
        else if (utilVal >= 50) status = 'partially_utilized';
        else if (utilVal > 0) status = 'not_utilized';
        else status = 'pending';

        return {
          id: idx + 1,
          activityId: idx + 1,
          activityName: item.activityName,
          subheadId: 0,
          subheadName: '',
          headId: 0,
          headName: '',
          code: `ACT-${String(idx + 1).padStart(3, '0')}`,
          name: item.activityName,
          description: '',
          expenditureType: 'non-recurring', // Default, API doesn't provide this
          sanctionedAmount: item.sanctioned,
          spentAmount: item.spent,
          unspentAmount: item.sanctioned - item.spent,
          utilizationPercentage: utilVal,
          status,
          schoolId: parseInt(kvId || '0'),
          schoolName: activityWiseResponse.data.profile.schoolName,
        };
      });

      setSchool(prev => prev ? { ...prev, activities } : null);
    }
  }, [activityWiseResponse, kvId]);

  // Update trends when monthly trend API responds
  useEffect(() => {
    if (monthlyTrendResponse?.success && monthlyTrendResponse.data) {
      const monthlyData = monthlyTrendResponse.data;
      
      const trends: MonthlyTrendData[] = Object.entries(monthlyData).map(([monthKey, data]) => {
        const utilVal = typeof data.totalUtilization === 'string'
          ? parseFloat(data.totalUtilization.replace('%', ''))
          : data.totalUtilization || 0;

        // Extract year from monthKey (e.g., "Jan 2026" -> 2026)
        const yearMatch = monthKey.match(/\d{4}/);
        const year = yearMatch ? parseInt(yearMatch[0]) : (selectedYear || new Date().getFullYear());

        return {
          month: monthKey,
          year,
          sanctioned: data.totalSanctioned,
          received: data.totalReceived,
          spent: data.totalSpent,
          utilization: utilVal,
          recurringSpent: 0, // Not provided in API
          nonRecurringSpent: 0, // Not provided in API
        };
      });

      setSchool(prev => prev ? { ...prev, trends } : null);
    }
  }, [monthlyTrendResponse, selectedYear]);

  const getStatusBadge = (status: string) => {
    const styles = {
      fully_utilized: 'bg-green-100 text-green-700',
      partially_utilized: 'bg-yellow-100 text-yellow-700',
      not_utilized: 'bg-red-100 text-red-700',
      pending: 'bg-gray-100 text-gray-600',
    };
    const labels = {
      fully_utilized: 'Fully Utilized',
      partially_utilized: 'Partial',
      not_utilized: 'Not Utilized',
      pending: 'Pending',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="flex-grow flex flex-col items-center justify-center">
          <KVSLoader size={80} />
          <span className="mt-4 text-gray-600">Loading...</span>
        </div>
        <Footer />
      </div>
    );
  }

  const filteredActivities = school?.activities.filter(a =>
    activityFilter === 'all' ? true : a.expenditureType === activityFilter
  ) || [];

  // Convert head-wise data to Head format for chart
  const headsForChart: Head[] = school?.headWiseData.map((h, idx) => ({
    id: h.headId,
    code: h.headCode,
    name: h.headName,
    description: '',
    color: HEAD_COLORS[h.headCode],
    totalSanctioned: h.sanctioned,
    totalReceived: h.received,
    totalSpent: h.spent,
    totalUnspent: h.unspent,
    utilizationPercentage: h.utilization,
    targetUnits: h.targetUnits,
    completedUnits: h.completedUnits,
    efficiencyPercentage: h.efficiency,
    subheadCount: 0,
  })) || [];

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4 pt-2 sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* School Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-gray-900">{school?.schoolName}</h1>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    PM-SHRI
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {school?.stateName}, {school?.districtName}
                  </span>
                  <span className="flex items-center gap-1">
                    <Building2 className="w-4 h-4" />
                    {school?.roName}
                  </span>
                  <span className="flex items-center gap-1">
                    <Award className="w-4 h-4" />
                    Rank #{school?.rank}
                    {school?.trend === 'up' ? (
                      <TrendingUp className="w-4 h-4 text-green-500" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-500" />
                    )}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <YearDropdown
                value={selectedYear}
                /* Pass the value through unchanged. `val || 2025` turned the
                   dropdown's null ("All Year Data") into 2025, so choosing
                   All Years silently selected 2025-26 instead and the page
                   looked like it was ignoring the filter. */
                onChange={(val) => setSelectedYear(val)}
              />
              <UtilizationGauge value={school?.overallUtilization || 0} size="md" label="Overall Utilization" />
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: Building2 },
              { id: 'heads', label: '6 Heads Breakdown', icon: IndianRupee },
              { id: 'activities', label: 'Activities', icon: Activity },
              { id: 'expenses', label: 'Expense Details', icon: IndianRupee },
              { id: 'pillars', label: 'Pillar Wise Expenditure', icon: IndianRupee },
              { id: 'budget', label: 'Sanctioned Heads & Budget', icon: IndianRupee },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${activeTab === tab.id
                  ? 'text-purple-700 border-purple-600 bg-purple-50'
                  : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                  }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {isTabLoading && (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <KVSLoader size={80} color="#6B1786" />
              <p className="text-sm text-gray-400 mt-4">Loading…</p>
            </div>
          </div>
        )}

        {activeTab === 'overview' && !isTabLoading && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-sm text-gray-600 mb-1">Total Sanctioned <span className="text-xs text-gray-400">(₹ Lakhs)</span></div>
                <div className="text-2xl font-bold text-gray-900">{formatCurrency(school?.totalSanctioned || 0)}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-sm text-gray-600 mb-1">Total Released <span className="text-xs text-gray-400">(₹ Lakhs)</span></div>
                <div className="text-2xl font-bold text-purple-600">{formatCurrency(school?.totalReceived || 0)}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-sm text-gray-600 mb-1">Total Spent <span className="text-xs text-gray-400">(₹ Lakhs)</span></div>
                <div className="text-2xl font-bold text-green-600">{formatCurrency(school?.totalSpent || 0)}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-sm text-gray-600 mb-1">Performance Score</div>
                <div className="text-2xl font-bold text-orange-600">{school?.performanceScore.toFixed(1)}</div>
              </div>
            </div>

            {/* Financial summary — the same six rows, in the same order, that the
                Samagam source system shows for a KV (Views/pages/hq/sanctioned/
                all-expenditure-by-kvid.php): Sanctioned, Released, Bank Interest,
                Expenditure, Bank Charges, and a highlighted Unspent row.

                Interest and charges deliberately show N/A in the recurring and
                non-recurring columns — they are account-level amounts and are not
                attributable to either bucket. */}
            {(() => {
              const fin = apiResponse?.data?.financials;
              const num = (v: number | undefined) =>
                (v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 5, maximumFractionDigits: 5 });

              const relRec = fin?.recurringAmount ?? 0;
              const relNon = fin?.nonRecurringAmount ?? 0;
              const spRec = fin?.spentRec ?? 0;
              const spNon = fin?.spentNonRec ?? 0;
              const unTotal = fin?.unspentAmount ?? ((fin?.receivedAmount ?? 0) - (fin?.spentAmount ?? 0));

              const rows: Array<{ label: string; total: number; rec: number | null; non: number | null }> = [
                { label: 'Sanctioned', total: fin?.sanctionedAmount ?? 0, rec: fin?.sanctionedRec ?? null, non: fin?.sanctionedNonRec ?? null },
                { label: 'Released', total: fin?.receivedAmount ?? 0, rec: relRec, non: relNon },
                { label: 'Bank Interest', total: fin?.bankInterest ?? 0, rec: null, non: null },
                { label: 'Expenditure', total: fin?.spentAmount ?? 0, rec: spRec, non: spNon },
                { label: 'Bank Charges', total: fin?.bankCharges ?? 0, rec: null, non: null },
              ];

              const cell = (v: number | null) => (v === null
                ? <span className="text-gray-400">N/A</span>
                : num(v));

              return (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-sm font-bold text-gray-900">Financial Summary</h3>
                    <span className="text-xs text-gray-500">All figures in ₹ Lakhs</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Category</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Total</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Recurring</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Non-Recurring</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {rows.map((r) => (
                          <tr key={r.label} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5 font-semibold text-gray-800">{r.label}</td>
                            <td className="px-4 py-2.5 text-right text-gray-900">{num(r.total)}</td>
                            <td className="px-4 py-2.5 text-right text-gray-600">{cell(r.rec)}</td>
                            <td className="px-4 py-2.5 text-right text-gray-600">{cell(r.non)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className={`font-bold border-t-2 ${unTotal < 0 ? 'bg-amber-50 border-amber-300' : 'bg-gray-100 border-gray-300'}`}>
                          <td className="px-4 py-3 text-gray-900">Unspent <span className="font-normal text-xs text-gray-500">(Released − Expenditure)</span></td>
                          <td className={`px-4 py-3 text-right ${unTotal < 0 ? 'text-amber-800' : 'text-gray-900'}`}>{num(unTotal)}</td>
                          <td className={`px-4 py-3 text-right ${relRec - spRec < 0 ? 'text-amber-800' : 'text-gray-800'}`}>{num(relRec - spRec)}</td>
                          <td className={`px-4 py-3 text-right ${relNon - spNon < 0 ? 'text-amber-800' : 'text-gray-800'}`}>{num(relNon - spNon)}</td>
                        </tr>
                        {/* The utilisation gap: allocation sanctioned but not yet spent.
                            This is the denominator behind the gauge and both cards. */}
                        <tr className="font-bold bg-blue-50 border-t border-blue-200">
                          <td className="px-4 py-3 text-gray-900">Unused Allocation <span className="font-normal text-xs text-gray-500">(Sanctioned − Expenditure)</span></td>
                          <td className="px-4 py-3 text-right text-blue-800">{num(fin?.sanctionedGap ?? ((fin?.sanctionedAmount ?? 0) - (fin?.spentAmount ?? 0)))}</td>
                          <td className="px-4 py-3 text-right text-gray-800">{num(fin?.sanctionedGapRec ?? ((fin?.sanctionedRec ?? 0) - spRec))}</td>
                          <td className="px-4 py-3 text-right text-gray-800">{num(fin?.sanctionedGapNonRec ?? ((fin?.sanctionedNonRec ?? 0) - spNon))}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {!!fin?.basisGap && (
                    <div className="px-5 py-2.5 bg-blue-50 border-t border-blue-200 text-xs text-blue-900">
                      Expenditure above is spend booked against <strong>{String(fin?.year)}</strong>&apos;s sanctioned heads,
                      whenever it was paid — so it pairs like-for-like with Sanctioned.
                      {' '}<strong>{num(fin?.spentInYear)}</strong> was actually paid out during the year itself,
                      a difference of <strong>{num(fin?.basisGap)}</strong>. The Expense Details tab lists payments by date,
                      so it totals the second figure.
                    </div>
                  )}
                  {unTotal < 0 && (
                    <div className="px-5 py-2.5 bg-amber-50 border-t border-amber-200 text-xs text-amber-800">
                      Expenditure exceeds recorded releases. Fund releases for one or more years have not been entered —
                      FY2026 releases are absent for every school, and this school&apos;s FY2025 record holds only instalment 4.
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Expenditure Type Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <Activity className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Recurring Expenditure</div>
                    <div className="text-xs text-gray-500">Training, events, activities</div>
                  </div>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{formatCurrency(school?.recurringSpent || 0)}</div>
                    <div className="text-sm text-gray-500">of {formatCurrency(school?.recurringAmount || 0)} sanctioned</div>
                  </div>
                  <div className="text-lg font-bold text-blue-600">{school?.recurringUtilization.toFixed(1)}%</div>
                </div>
                <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${(school?.recurringUtilization || 0) > 100 ? 'bg-amber-500' : 'bg-blue-500'}`}
                    style={{ width: `${Math.min(school?.recurringUtilization || 0, 100)}%` }}
                  />
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-green-100">
                    <Building2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Non-Recurring Expenditure</div>
                    <div className="text-xs text-gray-500">Lab setup, equipment, infrastructure</div>
                  </div>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-2xl font-bold text-green-600">{formatCurrency(school?.nonRecurringSpent || 0)}</div>
                    <div className="text-sm text-gray-500">of {formatCurrency(school?.nonRecurringAmount || 0)} sanctioned</div>
                  </div>
                  <div className="text-lg font-bold text-green-600">{school?.nonRecurringUtilization.toFixed(1)}%</div>
                </div>
                <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${(school?.nonRecurringUtilization || 0) > 100 ? 'bg-amber-500' : 'bg-green-500'}`}
                    style={{ width: `${Math.min(school?.nonRecurringUtilization || 0, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'heads' && !isTabLoading && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Head-wise Distribution</h3>
                <HeadWiseDonutChart
                  data={headsForChart}
                  height={280}
                  valueType="sanctioned"
                />
              </div>
              <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Head-wise Breakdown</h3>
                <div className="space-y-3">
                  {school?.headWiseData.map((head) => (
                    <div key={head.headId} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded flex items-center justify-center text-white text-xs font-bold"
                            style={{ backgroundColor: HEAD_COLORS[head.headCode] }}
                          >
                            {head.headCode}
                          </div>
                          <span className="font-medium text-gray-900">{head.headName}</span>
                        </div>
                        <span className={`font-bold ${head.utilization >= 75 ? 'text-green-600' :
                          head.utilization >= 50 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                          {head.utilization.toFixed(1)}%
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-sm mb-2">
                        <div>
                          <span className="text-gray-500">Sanctioned: </span>
                          <span className="font-medium">{formatCurrency(head.sanctioned)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Received: </span>
                          <span className="font-medium text-blue-600">{formatCurrency(head.received)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Spent: </span>
                          <span className="font-medium text-green-600">{formatCurrency(head.spent)}</span>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.min(Number(head.utilization) || 0, 100)}%`,
                            backgroundColor: HEAD_COLORS[head.headCode],
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activities' && !isTabLoading && (
          <div className="space-y-4">
            {/* Filter */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-700">Filter:</span>
                {['all', 'recurring', 'non-recurring'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setActivityFilter(filter as typeof activityFilter)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activityFilter === filter
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                  >
                    {filter === 'all' ? 'All' : filter === 'recurring' ? 'Recurring' : 'Non-Recurring'}
                  </button>
                ))}
                <span className="ml-auto text-sm text-gray-500">
                  {filteredActivities.length} activities
                </span>
              </div>
            </div>

            {/* Activities List */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Activity</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Head</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Type</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Sanctioned</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Spent</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Utilization</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredActivities.map((activity) => (
                    <tr key={activity.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{activity.name}</div>
                        <div className="text-xs text-gray-500">{activity.code}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">{activity.headName}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${activity.expenditureType === 'recurring'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-green-100 text-green-700'
                          }`}>
                          {activity.expenditureType === 'recurring' ? 'Recurring' : 'Non-Recurring'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                        {formatCurrency(activity.sanctionedAmount)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-green-600">
                        {formatCurrency(activity.spentAmount)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${activity.utilizationPercentage >= 75 ? 'bg-green-500' :
                                activity.utilizationPercentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                              style={{ width: `${Math.min(activity.utilizationPercentage, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">{activity.utilizationPercentage.toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(activity.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'expenses' && !isTabLoading && (
          <div className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-sm text-gray-600 mb-1">Total Expenses</div>
                <div className="text-2xl font-bold text-purple-600">
                  {expenseDetailResponse?.data?.expenses?.length || 0}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-sm text-gray-600 mb-1">Total Amount</div>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(
                    expenseDetailResponse?.data?.expenses?.reduce((sum, exp) => sum + exp.expenditureAmount, 0) || 0
                  )}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-sm text-gray-600 mb-1">Total Quantity</div>
                <div className="text-2xl font-bold text-blue-600">
                  {expenseDetailResponse?.data?.expenses?.reduce((sum, exp) => sum + exp.expenditureQuantity, 0) || 0}
                </div>
              </div>
            </div>

            {/* Expenses Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Activity Name</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Quantity</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Unit Cost</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {expenseDetailResponse?.data?.expenses?.map((expense, index) => (
                      <tr key={expense.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{expense.activityName}</div>
                          <div className="text-xs text-gray-500">ID: {expense.id}</div>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-green-600">
                          {formatCurrency(expense.expenditureAmount)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {expense.expenditureQuantity}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-500">
                          {expense.expenditureQuantity > 0
                            ? formatCurrency(expense.expenditureAmount / expense.expenditureQuantity)
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {new Date(expense.expenditureDate).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                      </tr>
                    )) || (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                            No expense records found
                          </td>
                        </tr>
                      )}
                  </tbody>
                  {!!expenseDetailResponse?.data?.expenses?.length && (
                    <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                      <tr>
                        <td className="px-4 py-3 text-sm font-bold text-gray-700" colSpan={2}>
                          Total — {expenseDetailResponse.data.expenses.length} records
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-green-700">
                          {formatCurrency(
                            expenseDetailResponse.data.expenses.reduce((sum, exp) => sum + exp.expenditureAmount, 0)
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-sm font-bold text-blue-700">
                          {expenseDetailResponse.data.expenses.reduce((sum, exp) => sum + exp.expenditureQuantity, 0)}
                        </td>
                        {/* No total for Unit Cost: it is a per-line rate, so adding the
                            rate of an interactive panel to that of a magic slate would
                            be a meaningless number. */}
                        <td className="px-4 py-3 text-right text-sm text-gray-400">—</td>
                        <td className="px-4 py-3" />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'budget' && !isTabLoading && (() => {
          const bd = budgetResponse?.data;
          const n5 = (v: number) => (v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 5, maximumFractionDigits: 5 });
          const statusPill = (s: string) => {
            const tone = s === 'Completed' ? 'bg-green-100 text-green-700'
              : s === 'Continue' ? 'bg-blue-100 text-blue-700'
                : s === 'Not Started' ? 'bg-gray-200 text-gray-600'
                  : 'bg-amber-100 text-amber-700';
            return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${tone}`}>{s}</span>;
          };

          if (!bd || bd.data.length === 0) {
            return <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-500">No sanctioned heads recorded for this school.</div>;
          }

          return (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-sm text-gray-600 mb-1">Sanctioned Heads</div>
                  <div className="text-2xl font-bold text-purple-600">{bd.totals.rows}</div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-sm text-gray-600 mb-1">Sanctioned <span className="text-xs text-gray-400">(₹ Lakhs)</span></div>
                  <div className="text-2xl font-bold text-gray-900">{n5(bd.totals.amount)}</div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-sm text-gray-600 mb-1">Expenditure <span className="text-xs text-gray-400">(₹ Lakhs)</span></div>
                  <div className="text-2xl font-bold text-green-600">{n5(bd.totals.expenditureAmount)}</div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-sm text-gray-600 mb-1">Physical Units</div>
                  <div className="text-2xl font-bold text-blue-600">{bd.totals.physicalUnits.toLocaleString('en-IN')}</div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-bold text-gray-900">Sanctioned Heads &amp; Budget</h3>
                  <span className="text-xs text-gray-500">
                    Every sanctioned head, including those not yet started. Amounts in ₹ Lakhs.
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Sn.</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Head</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Sub Head</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Activity</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Sub Activity</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Activity Type</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Physical Units</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Unit Cost</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Amount</th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Status</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Expenditure Amount</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Expenditure Unit</th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Year</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {bd.data.map((r) => (
                        <tr key={r.sanctionedID} className="hover:bg-gray-50 align-top">
                          <td className="px-3 py-2 text-gray-500">{r.sn}</td>
                          <td className="px-3 py-2 text-gray-700">{r.head}</td>
                          <td className="px-3 py-2 text-gray-700">{r.subHead}</td>
                          <td className="px-3 py-2 text-gray-700">{r.activity}</td>
                          <td className="px-3 py-2 font-medium text-gray-900">{r.subActivity}</td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{r.activityType}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{r.physicalUnits}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{n5(r.unitCost)}</td>
                          <td className="px-3 py-2 text-right font-medium text-purple-700">{n5(r.amount)}</td>
                          <td className="px-3 py-2 text-center whitespace-nowrap">{statusPill(r.status)}</td>
                          <td className="px-3 py-2 text-right font-medium text-green-700">{n5(r.expenditureAmount)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{r.expenditureUnit}</td>
                          <td className="px-3 py-2 text-center text-gray-600 whitespace-nowrap">
                            {r.year ? r.year + '-' + String(r.year + 1).slice(-2) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                      <tr className="font-bold">
                        <td className="px-3 py-2.5 text-gray-900" colSpan={6}>Total — {bd.totals.rows} heads</td>
                        <td className="px-3 py-2.5 text-right text-gray-800">{bd.totals.physicalUnits.toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2.5" />
                        <td className="px-3 py-2.5 text-right text-purple-800">{n5(bd.totals.amount)}</td>
                        <td className="px-3 py-2.5" />
                        <td className="px-3 py-2.5 text-right text-green-800">{n5(bd.totals.expenditureAmount)}</td>
                        <td className="px-3 py-2.5 text-right text-gray-800">{bd.totals.expenditureUnit.toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2.5" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        {activeTab === 'pillars' && !isTabLoading && (() => {
          const pw = pillarWiseResponse?.data;
          // Plain 5-decimal numbers in the grid (the ₹ sign on every cell of a
          // 7-column table makes the figures much harder to compare down a column).
          const n5 = (v: number) => (v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 5, maximumFractionDigits: 5 });
          const Badges = ({ s, x }: { s: PillarMoneyUI; x: PillarMoneyUI }) => (
            <div className="flex flex-wrap gap-1.5 text-[11px] font-semibold">
              <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700">Sanctioned {n5(s.total)}</span>
              <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700">Spent Rec {n5(x.rec)}</span>
              <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800">Spent Non-Rec {n5(x.nonRec)}</span>
              <span className="px-2 py-0.5 rounded bg-green-100 text-green-700">Spent Total {n5(x.total)}</span>
            </div>
          );

          if (!pw || pw.pillars.length === 0) {
            return <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-500">No pillar-wise expenditure recorded for this school.</div>;
          }

          return (
            <div className="space-y-6">
              <div className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                All figures in ₹ Lakhs.
                {pw.year !== 'Overall' && (
                  <>
                    {' '}<span className="font-semibold text-blue-800">Spent</span> here is all expenditure booked against funds
                    <span className="font-semibold"> sanctioned in {String(pw.year)}</span>, whenever it was spent — so it pairs
                    like-for-like with the Sanctioned column. The Overview and Expense Details tabs instead count spend by the
                    date it occurred, so their totals for a single year will differ.
                  </>
                )}
              </div>

              {pw.pillars.map((pillar) => (
                <div key={pillar.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-base font-bold text-gray-900">{pillar.title}</h3>
                    <Badges s={pillar.sanctioned} x={pillar.spent} />
                  </div>

                  <div className="divide-y divide-gray-100">
                    {pillar.subHeads.map((sh) => (
                      <div key={sh.id} className="px-5 py-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                          <h4 className="text-sm font-semibold text-gray-800">{sh.title}</h4>
                          <Badges s={sh.sanctioned} x={sh.spent} />
                        </div>

                        {sh.activities.map((ac) => (
                          <div key={ac.id} className="mb-4 last:mb-0">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                              <div className="text-sm font-medium text-gray-700">{ac.title}</div>
                              <Badges s={ac.sanctioned} x={ac.spent} />
                            </div>
                            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Sub Activity</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">San Rec</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">San Non-Rec</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">San Total</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Spent Rec</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Spent Non-Rec</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Spent Total</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {ac.subActivities.map((sa) => (
                                    <tr key={sa.id} className="hover:bg-gray-50">
                                      <td className="px-3 py-2 text-gray-800">{sa.title}</td>
                                      <td className="px-3 py-2 text-right text-gray-600">{n5(sa.sanctioned.rec)}</td>
                                      <td className="px-3 py-2 text-right text-gray-600">{n5(sa.sanctioned.nonRec)}</td>
                                      <td className="px-3 py-2 text-right font-medium text-purple-700">{n5(sa.sanctioned.total)}</td>
                                      <td className="px-3 py-2 text-right text-gray-600">{n5(sa.spent.rec)}</td>
                                      <td className="px-3 py-2 text-right text-gray-600">{n5(sa.spent.nonRec)}</td>
                                      <td className="px-3 py-2 text-right font-medium text-green-700">{n5(sa.spent.total)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Pillar Summary + Grand Total — the closing table of the reference report. */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 bg-purple-700 text-white font-bold text-sm">Pillar Summary</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Pillar Name</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">San Rec</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">San Non-Rec</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">San Total</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Spent Rec</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Spent Non-Rec</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Spent Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {pw.summary.map((p) => (
                        <tr key={p.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium text-gray-800">{p.title}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{n5(p.sanctioned.rec)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{n5(p.sanctioned.nonRec)}</td>
                          <td className="px-3 py-2 text-right font-medium text-purple-700">{n5(p.sanctioned.total)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{n5(p.spent.rec)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{n5(p.spent.nonRec)}</td>
                          <td className="px-3 py-2 text-right font-medium text-green-700">{n5(p.spent.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                      <tr className="font-bold">
                        <td className="px-3 py-2.5 text-gray-900">Grand Total</td>
                        <td className="px-3 py-2.5 text-right text-gray-800">{n5(pw.grandTotal.sanctioned.rec)}</td>
                        <td className="px-3 py-2.5 text-right text-gray-800">{n5(pw.grandTotal.sanctioned.nonRec)}</td>
                        <td className="px-3 py-2.5 text-right text-purple-800">{n5(pw.grandTotal.sanctioned.total)}</td>
                        <td className="px-3 py-2.5 text-right text-gray-800">{n5(pw.grandTotal.spent.rec)}</td>
                        <td className="px-3 py-2.5 text-right text-gray-800">{n5(pw.grandTotal.spent.nonRec)}</td>
                        <td className="px-3 py-2.5 text-right text-green-800">{n5(pw.grandTotal.spent.total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}


      </main>

      <Footer />
    </div>
  );
};

export default SchoolDetailsScreen;
